import { PostgrestFilterBuilder } from '@supabase/postgrest-js';
import { SupabaseClient } from '@supabase/supabase-js';
import {
  PaginationOptions,
  PaginatedResponse,
  getOffsetFromPage,
  calculatePaginationMeta,
  DEFAULT_LIMIT
} from '../types/pagination.types';

/**
 * Apply pagination to a Supabase query
 * @param query The base query builder
 * @param options Pagination options
 * @param mapFunction Function to map raw data to entities
 * @returns Either an array or paginated response based on options
 */
export async function applyPagination<T, R>(
  query: PostgrestFilterBuilder<any, any, any>,
  options?: PaginationOptions,
  mapFunction?: (data: any) => R
): Promise<R[] | PaginatedResponse<R>> {
  // Cursor-based pagination
  if (options?.cursor) {
    const limit = options.limit || DEFAULT_LIMIT;
    const cursor = options.cursor;

    // Apply ordering if specified (required for cursor pagination)
    const orderField = options.orderBy || 'id';
    const ascending = options.order === 'asc';
    query = query.order(orderField, { ascending });

    // Apply cursor filter
    if (cursor) {
      const operator = ascending ? 'gt' : 'lt';
      query = query.filter(orderField, operator, cursor);
    }

    // Get one extra item to check if there's a next page
    query = query.limit(limit + 1);

    const { data, error } = await query;
    if (error) throw error;

    const items = data || [];
    const hasMore = items.length > limit;

    // Remove the extra item if present
    if (hasMore) {
      items.pop();
    }

    // Calculate cursors
    const nextCursor = hasMore && items.length > 0
      ? items[items.length - 1][orderField]
      : undefined;
    const previousCursor = cursor; // The current cursor becomes the previous

    return {
      data: mapFunction
        ? items.map(mapFunction)
        : items as R[],
      pagination: {
        page: 0, // Not applicable for cursor pagination
        limit,
        total: 0, // Not available in cursor pagination
        totalPages: 0, // Not available in cursor pagination
        hasNextPage: hasMore,
        hasPreviousPage: !!cursor,
        nextCursor,
        previousCursor
      }
    };
  }

  // Offset-based pagination with page number
  if (options?.page) {
    const page = options.page;
    const limit = options.limit || DEFAULT_LIMIT;
    const offset = getOffsetFromPage(page, limit);

    // Apply ordering if specified
    if (options.orderBy) {
      query = query.order(options.orderBy, { ascending: options.order === 'asc' });
    }

    // Apply pagination with range
    query = query.range(offset, offset + limit - 1);

    // Execute query
    const { data, error, count } = await query;
    if (error) throw error;

    // If count is available, use it; otherwise estimate
    const totalCount = count !== undefined && count !== null 
      ? count 
      : (data || []).length < limit 
        ? (page - 1) * limit + (data || []).length 
        : page * limit + 1;

    return {
      data: mapFunction
        ? (data || []).map(mapFunction)
        : (data || []) as R[],
      pagination: calculatePaginationMeta(totalCount, page, limit)
    };
  }

  // Non-paginated request
  if (options?.orderBy) {
    query = query.order(options.orderBy, { ascending: options.order === 'asc' });
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset !== undefined) {
    const limit = options.limit || 10;
    query = query.range(options.offset, options.offset + limit - 1);
  }

  const { data, error } = await query;
  if (error) throw error;

  return mapFunction
    ? (data || []).map(mapFunction)
    : (data || []) as R[];
}

/**
 * Helper to execute paginated query with separate count
 * Use when the main query is complex and needs to be built separately from count
 */
export async function executePaginatedQuery<T, R>(
  dataQuery: PostgrestFilterBuilder<any, any, any>,
  countQuery: PostgrestFilterBuilder<any, any, any>,
  page: number,
  limit: number,
  mapFunction?: (data: any) => R
): Promise<PaginatedResponse<R>> {
  const offset = getOffsetFromPage(page, limit);

  // Get total count
  const { count, error: countError } = await countQuery;
  if (countError) throw countError;

  // Get paginated data
  const { data, error } = await dataQuery.range(offset, offset + limit - 1);
  if (error) throw error;

  return {
    data: mapFunction
      ? (data || []).map(mapFunction)
      : (data || []) as R[],
    pagination: calculatePaginationMeta(count || 0, page, limit)
  };
}

/**
 * Execute cursor-based paginated query
 * @param query Base query builder
 * @param cursor Current cursor value
 * @param limit Number of items to fetch
 * @param orderField Field to order by (must be unique and sortable)
 * @param ascending Sort direction
 * @param mapFunction Optional mapping function
 * @returns Paginated response with cursor information
 */
export async function executeCursorPaginatedQuery<T, R>(
  query: PostgrestFilterBuilder<any, any, any>,
  cursor: string | undefined,
  limit: number,
  orderField: string,
  ascending: boolean = true,
  mapFunction?: (data: any) => R
): Promise<PaginatedResponse<R>> {
  // Apply ordering
  query = query.order(orderField, { ascending });

  // Apply cursor filter if provided
  if (cursor) {
    const operator = ascending ? 'gt' : 'lt';
    query = query.filter(orderField, operator, cursor);
  }

  // Get one extra item to check if there's more
  query = query.limit(limit + 1);

  const { data, error } = await query;
  if (error) throw error;

  const items = data || [];
  const hasMore = items.length > limit;

  // Remove the extra item
  if (hasMore) {
    items.pop();
  }

  // Calculate cursors
  const nextCursor = hasMore && items.length > 0
    ? items[items.length - 1][orderField]
    : undefined;
  const previousCursor = cursor;

  return {
    data: mapFunction
      ? items.map(mapFunction)
      : items as R[],
    pagination: {
      page: 0, // Not applicable
      limit,
      total: 0, // Not available in cursor pagination
      totalPages: 0, // Not available in cursor pagination
      hasNextPage: hasMore,
      hasPreviousPage: !!cursor,
      nextCursor,
      previousCursor
    }
  };
}
